import { useLocation } from "@tanstack/react-router";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from "react";
import { useTabsActions, useTabsState } from "@/contexts/tabs-context";
import { matchWorkspaceLocation } from "@/routing/core/locations";

type SettingsOrigin = { source: "workspace"; tabId: string } | { source: "home" } | null;

interface SettingsOriginValue {
  goBackFromSettings: () => void;
  getSettingsBackLabel: () => string;
}

const SettingsOriginContext = createContext<SettingsOriginValue | null>(null);

export function SettingsOriginProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { tabs } = useTabsState();
  const { setActiveTab, goHome } = useTabsActions();
  const originRef = useRef<SettingsOrigin>(null);
  const previousPathRef = useRef(location.pathname);

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    const enteringSettings =
      currentPath.startsWith("/settings") && !previousPath.startsWith("/settings");

    if (enteringSettings) {
      const previousWorkspace = matchWorkspaceLocation(previousPath);
      if (previousWorkspace) {
        originRef.current = { source: "workspace", tabId: previousWorkspace.tabId };
      } else if (previousPath === "/") {
        originRef.current = { source: "home" };
      } else {
        originRef.current = null;
      }
    }

    previousPathRef.current = currentPath;
  }, [location.pathname]);

  const goBackFromSettings = useCallback(() => {
    const origin = originRef.current;
    if (origin?.source === "workspace" && tabs.some((tab) => tab.tabId === origin.tabId)) {
      setActiveTab(origin.tabId);
      return;
    }

    goHome();
  }, [tabs, setActiveTab, goHome]);

  const getSettingsBackLabel = useCallback(() => {
    const origin = originRef.current;
    if (origin?.source === "workspace") {
      const tab = tabs.find((item) => item.tabId === origin.tabId);
      if (tab?.kind === "project") return "Back to Project";
      if (tab?.kind === "admin") return "Back to Admin Portal";
    }

    return "Back to Home";
  }, [tabs]);

  return (
    <SettingsOriginContext.Provider value={{ goBackFromSettings, getSettingsBackLabel }}>
      {children}
    </SettingsOriginContext.Provider>
  );
}

export function useSettingsOrigin(): SettingsOriginValue {
  const value = useContext(SettingsOriginContext);
  if (!value) {
    throw new Error("useSettingsOrigin must be used within SettingsOriginProvider");
  }

  return value;
}
